import ModuleTypeEnum from "../enums/ModuleTypeEnum";
import Category from "../models/category/Category";
import Module from "../models/module/Module";
import { ModuleRepository, PageRepository } from "../repository";
import JsonHelper from "./JsonHelper";

class PageHelper {
    static _props = {};

    static generateInternalLinkList = (props, settings = {}) => Promise.all([
        PageRepository.tree(),
        ModuleRepository.all(),
        ModuleRepository.getData()
    ])
        .then(result => {
            PageHelper._props = { ...props, ...{ settings: settings } };

            let modules = result[1].map(module => Module.fromJson(module.module));

            let allowedCategories = [];
            let moduleData = {};

            // Modules used via 'use_references' of another module are no separate pages (overrules show_content)
            let referencedModuleIds = [];
            modules.forEach(module => {
                if (!Module.getSetting(module, 'use_references')) return;

                let referenceModules = Module.getSetting(module, 'reference_modules', []);
                if (!Array.isArray(referenceModules)) return;

                referenceModules.forEach(id => referencedModuleIds.push(parseInt(id)));
            });

            // Modules
            if (modules.length) {
                modules.forEach(module => {
                    if (!(module.id in moduleData)) {
                        moduleData[module.id] = {
                            module: module,
                            referenced: referencedModuleIds.indexOf(parseInt(module.id)) > -1,
                            items: []
                        };
                    }

                    if (module.isOfType(ModuleTypeEnum.CATEGORY)) {
                        moduleData[module.id].items = result[2].categories.filter(c => c.category.module === module.id);
                        moduleData[module.id].items.forEach(category => allowedCategories.push(category));
                    }
                });

                modules.forEach(module => {
                    if (module.isOfType(ModuleTypeEnum.CATEGORY)) {
                        let moduleCategories = result[2].categories.filter(c => c.category.module === module.id);

                        let showContent = Module.getSetting(module, 'content_with_blocks') || Module.getSetting(module);
                        let showContentSubcategories = Module.getSetting(module, 'subcategories_content_with_blocks') || Module.getSetting(module, 'subcategories_show_content');

                        moduleCategories.forEach(category => {
                            if (
                                (!category.category.parent && showContent) ||
                                (category.children.length > 0 && showContentSubcategories)
                            ) {
                                allowedCategories.push(category);
                            } else if (category.category.parent && showContentSubcategories) {
                                allowedCategories.push(category);
                            }
                        });
                    } else if (module.isOfType(ModuleTypeEnum.TEAM)) {
                        if (Module.getSetting(module, 'show_content')) {
                            moduleData[module.id].items = result[2].team.filter(item => item.team.module === module.id);
                        }
                    } else if ([ModuleTypeEnum.BLOG, ModuleTypeEnum.EVENT, ModuleTypeEnum.PROJECT, ModuleTypeEnum.NEWS, ModuleTypeEnum.PRODUCT].indexOf(module.type) > -1) {
                        if (module.type === ModuleTypeEnum.BLOG) {
                            moduleData[module.id].items = result[2].blogs.filter(item => item.blog.module === module.id);
                        } else if (module.type === ModuleTypeEnum.EVENT) {
                            moduleData[module.id].items = result[2].events.filter(item => item.event.module === module.id);
                        } else if (module.type === ModuleTypeEnum.PROJECT) {
                            moduleData[module.id].items = result[2].projects.filter(item => item.project.module === module.id);
                        } else if (module.type === ModuleTypeEnum.NEWS) {
                            moduleData[module.id].items = result[2].news.filter(item => item.news.module === module.id);
                        } else if (module.type === ModuleTypeEnum.PRODUCT) {
                            moduleData[module.id].items = result[2].products.filter(item => item.product.module === module.id);
                        }
                    }
                });
            }

            let linkList = [];
            this.assembleList(linkList, {
                pages: result[0],
                moduleData: moduleData,
                categoryTree: allowedCategories.length > 0 ? Category.renderTree(allowedCategories) : {}
            });

            // Home page -> no children
            if (linkList.length > 0) {
                linkList[0].children = [];
            }

            return linkList;
        });

    static assembleList = (linkList, data) => {
        data.pages.forEach((page) => {
            // let content = page.page.contents.find(content => content.language === this._props.language);
            let content = page.page.contents[PageHelper._props.language];
            if (!content) {
                content = page.page.contents.find(c => c.language === PageHelper._props.language);
            }

            let record = {
                id: page.id,
                title: content?.title,
                url: (PageHelper._props.settings?.includeDomain ? page.domain : '') + '/' + (content ? content.language : 'nl') + '/permalink/page/' + page.id,
                disabled: page.page.type === 'url',
                children: []
            };

            if (page.children.length > 0) {
                record.children = this.assembleList(record.children, { ...data, ...{ pages: page.children } });
            }

            if (page.page.modules) {
                JsonHelper.tryParse(page.page.modules).forEach(pageModule => {
                    if (!(pageModule in data.moduleData)) return;
                    if (data.moduleData[pageModule].referenced) return;

                    let module = data.moduleData[pageModule].module;
                    let moduleCategory = module.getSetting('use_categories') ? module.getSetting('module') : null;

                    if (pageModule in data.categoryTree) {
                        const pageModulesCategories = JsonHelper.tryParse(page.page.module_categories);

                        let pageCategories = {};
                        if (pageModulesCategories && Object.keys(pageModulesCategories).length > 0) {
                            Object.values(pageModulesCategories).forEach(id => {
                                if (id in data.categoryTree[pageModule]) {
                                    pageCategories[id] = data.categoryTree[pageModule][id];
                                }
                            });
                        } else {
                            pageCategories = data.categoryTree[pageModule];
                        }

                        record.children = [...record.children, ...this.assembleLinkListCategories(
                            [],
                            page,
                            pageCategories
                        )];
                    } else if (moduleCategory in data.categoryTree) {
                        record.children = [...record.children, ...this.assembleLinkListCategories(
                            [],
                            page,
                            data.categoryTree[moduleCategory],
                            1,
                            data.moduleData[pageModule]
                        )];
                    } else if (pageModule in data.moduleData) {
                        record.children = [...record.children, ...this.assembleLinkList([], page, data.moduleData[pageModule])];
                    }
                });
            }

            linkList.push(record);
        });

        return linkList;
    }

    static assembleLinkListCategories = (list, page, categories, depth = 1, data = {}) => {
        Object.keys(categories).forEach(id => {
            let category = categories[id];

            let content = category.category.contents[PageHelper._props.language];
            if (!content) {
                content = category.category.contents.find(content => content.language === PageHelper._props.language);
            }

            let record = {
                id: category.category.id,
                title: content?.title,
                url: (PageHelper._props.settings?.includeDomain ? page.domain : '') + '/' + (content ? content.language : 'nl') + '/permalink/category/' + category.category.id + '/' + page.id,
                children: []
            };

            if (Object.keys(category.children).length > 0) {
                record.children = this.assembleLinkListCategories(record.children, page, category.children, depth + 1, data);
            }

            if (data.items?.length > 0) {
                let categoryItems = data.items.filter(item => item[data.module.type].category === parseInt(id));
                if (categoryItems.length > 0) {
                    record.children = [...record.children, ...this.assembleLinkList([], page, {
                        module: data.module, items: categoryItems
                    })];
                }
            }

            list.push(record);
        });

        return list;
    }

    static assembleLinkList = (list, page, data, depth = 1) => {
        let type = data.module.type;
        data.items.forEach(item => {
            // let content = item[type].contents.find(content => content.language === this._props.language);
            let content = item[type].contents[PageHelper._props.language];
            if (!content) {
                content = item[type].contents.find(content => content.language === PageHelper._props.language);
            }

            let record = {
                id: item.id,
                title: content?.title,
                url: (PageHelper._props.settings?.includeDomain ? page.domain : '') + '/' + (content ? content.language : 'nl') + '/permalink/' + type + '/' + item.id + '/' + page.id + '/' + data.module.id,
                children: []
            };

            if (item.children && item.children.length > 0) {
                record.children = this.assembleLinkList(record.children, page, { module: data.module, items: item.children }, depth + 1);
            }

            list.push(record);
        });

        return list;
    }
}

export default PageHelper;